import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import CustomRangePicker from 'components/CustomRangePicker';
import { lighten, ThemeProvider } from '@mui/material/styles';
import { useMaterialUIController } from 'context';
import { getTablesTheme } from 'components/CustomThemes';
import { Box, Typography, Stack, Button } from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleGlobalFilterButton,
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
} from 'material-react-table';
import PropTypes from 'prop-types';
import {
  TABLE_LOCALE,
  TABLE_STATE_KEYS,
  TABLE_TYPE,
} from 'shared/constants/tableOptions';
import { USER_DATA } from 'shared/constants/users';
import intl from 'react-intl-universal';
import { RefreshButton } from 'components';
import EditIcon from '@mui/icons-material/Edit';

import logger from 'utils/logger';

const log = logger.createLogger('KenshinTable');

const propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  refreshData: PropTypes.func,
  tableTitle: PropTypes.string.isRequired,
  timeRangeStart: PropTypes.instanceOf(Date).isRequired,
  timeRangeEnd: PropTypes.instanceOf(Date).isRequired,
  updateTimeRangeStart: PropTypes.func.isRequired,
  updateTimeRangeEnd: PropTypes.func.isRequired,
  handleBatchEditClick: PropTypes.func,
  handleRowModelChange: PropTypes.func,
  tableType: PropTypes.string,
};

const KenshinTable = ({
  columns,
  data,
  tableTitle,
  timeRangeStart,
  timeRangeEnd,
  updateTimeRangeStart,
  updateTimeRangeEnd,
  refreshData,
  handleBatchEditClick,
  handleRowModelChange,
  tableType = TABLE_TYPE.KENSHIN,
}) => {
  const tableStateSorting =
    tableType === TABLE_TYPE.KENSHIN_ASSIGNMENT
      ? TABLE_STATE_KEYS.KENSHIN_ASSIGNMENT_SORTING
      : TABLE_STATE_KEYS.KENSHIN_SORTING;

  const tableStatePagination =
    tableType === TABLE_TYPE.KENSHIN_ASSIGNMENT
      ? TABLE_STATE_KEYS.KENSHIN_ASSIGNMENT_PAGINATION
      : TABLE_STATE_KEYS.KENSHIN_PAGINATION;

  const locale = localStorage.getItem(USER_DATA.PREFFERED_LANGUAGE);

  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});

  // Closing the picker when the location changes
  useEffect(() => {
    if (pathname !== pathnameRef.current) {
      setIsStartDatePickerOpen(false);
      setIsEndDatePickerOpen(false);
    }
  }, [pathname]);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const [sortFilters, setSortFilters] = useState(() => {
    const saved = sessionStorage.getItem(tableStateSorting);
    return saved ? JSON.parse(saved) : [];
  });

  const [pagination, setPagination] = useState(() => {
    const saved = sessionStorage.getItem(tableStatePagination);
    return saved ? JSON.parse(saved) : { pageIndex: 0, pageSize: 10 };
  });

  useEffect(() => {
    sessionStorage.setItem(tableStatePagination, JSON.stringify(pagination));
  }, [pagination]);

  useEffect(() => {
    sessionStorage.setItem(tableStateSorting, JSON.stringify(sortFilters));
  }, [sortFilters]);

  const table = useMaterialReactTable({
    columns,
    data,
    muiTableContainerProps: {
      sx: { maxHeight: '950px' },
    },
    localization: {
      ...TABLE_LOCALE[locale],
      toggleSelectAll: intl.get('share_mrt2_text_label_body_toggle_select_all'),
      noRecordsToDisplay: intl.get('share_muitable_text_label_body_no_match'),
    },
    // TODO Dark Mode
    muiTableHeadProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiTablePaperProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: '#ffffff',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: '#ffffff', // Set cells background to white
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: '#ffffff', // Light gray for header contrast
      },
    },
    enableColumnFilterModes: false,
    enableStickyHeader: true,
    enableGrouping: true,
    enableColumnActions: false,
    enableRowActions: false,
    enableColumnDragging: false,
    enableSelectAll: handleBatchEditClick ? true : false,
    columnFilterDisplayMode: 'popover',
    enableFacetedValues: true,
    enableRowSelection: handleBatchEditClick ? true : false,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    autoResetPageIndex: false,
    onPaginationChange: setPagination,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: false,
      density: 'compact',
    },
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 20, 30],
    },
    state: {
      columnFilters,
      globalFilter,
      sorting: sortFilters,
      isLoading: !data,
      pagination,
      rowSelection,
    },
    getRowId: (row) => row.index,
    onColumnFiltersChange: (updatedColumnFilters) => {
      setColumnFilters(updatedColumnFilters);
      setIsStartDatePickerOpen(false);
      setIsEndDatePickerOpen(false);
      setTimeout(() => {
        handleRowModelChange &&
          handleRowModelChange(table.getFilteredRowModel().rows);
      }, 500);
    },
    onSortingChange: (updatedSortFilters) => {
      setSortFilters(updatedSortFilters);
      setIsStartDatePickerOpen(false);
      setIsEndDatePickerOpen(false);
      setTimeout(() => {
        handleRowModelChange && handleRowModelChange(table.getRowModel().rows);
      }, 0);
    },
    onGlobalFilterChange: (updatedGlobalFilter) => {
      setGlobalFilter(updatedGlobalFilter);
      setIsStartDatePickerOpen(false);
      setIsEndDatePickerOpen(false);
      setTimeout(() => {
        handleRowModelChange &&
          handleRowModelChange(table.getFilteredRowModel().rows);
      }, 500);
    },
    onRowSelectionChange: (updater) => {
      setIsStartDatePickerOpen(false);
      setIsEndDatePickerOpen(false);
      setRowSelection(updater);
    },
    getFacetedUniqueValues: (table, columnId) => () => {
      const uniqueValueMap = new Map();
      table.getPreFilteredRowModel().rows.forEach((row) => {
        const value = row.original[columnId];
        if (Array.isArray(value)) {
          value.forEach((tag) => {
            uniqueValueMap.set(tag, (uniqueValueMap.get(tag) || 0) + 1);
          });
        } else if (value !== undefined && value !== null) {
          // Handle non-array values
          uniqueValueMap.set(value, (uniqueValueMap.get(value) || 0) + 1);
        }
      });
      return uniqueValueMap;
    },
    renderTopToolbar: ({ table }) => {
      const selectedRows = table.getSelectedRowModel().rows;
      return (
        <Stack sx={{ m: '2rem 0' }}>
          <Box sx={{ pl: '10px' }}>
            <Typography variant="h4" style={{ fontWeight: 'bold' }}>
              {tableTitle}
            </Typography>
          </Box>
          <Box
            sx={(theme) => ({
              backgroundColor: lighten(theme.palette.background.default, 0.05),
              display: 'flex',
              gap: '0.5rem',
              p: '8px',
              justifyContent: 'space-between',
            })}>
            <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <CustomRangePicker
                isStartDatePickerOpen={isStartDatePickerOpen}
                setIsStartDatePickerOpen={setIsStartDatePickerOpen}
                isEndDatePickerOpen={isEndDatePickerOpen}
                setIsEndDatePickerOpen={setIsEndDatePickerOpen}
                timeRangeStart={timeRangeStart}
                timeRangeEnd={timeRangeEnd}
                updateTimeRangeStart={updateTimeRangeStart}
                updateTimeRangeEnd={updateTimeRangeEnd}
                darkMode={darkMode}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {}
              <MRT_GlobalFilterTextField table={table} />
              {}
              <MRT_ToggleGlobalFilterButton table={table} />
              {}
              <MRT_ShowHideColumnsButton table={table} />
              <RefreshButton
                darkMode={darkMode}
                tooltipTitle={intl.get(
                  'dashboard_refresh_button_tooltip_title'
                )}
                handleRefreshClick={() => {
                  refreshData();
                  table.resetRowSelection();
                }}
              />
              {handleBatchEditClick && (
                <Button
                  color="info"
                  disabled={selectedRows.length === 0}
                  onClick={() => {
                    handleBatchEditClick(selectedRows);
                    table.resetRowSelection();
                  }}
                  variant="contained"
                  startIcon={<EditIcon />}>
                  {intl.get(
                    'dashboard_received_history_kenshin_table_button_edit'
                  )}
                </Button>
              )}
            </Box>
          </Box>
        </Stack>
      );
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        setIsStartDatePickerOpen(false);
        setIsEndDatePickerOpen(false);
        row.getToggleSelectedHandler()(event);
      },
      sx: {
        cursor: 'pointer',
      },
    }),
  });

  return (
    <Box>
      <ThemeProvider theme={getTablesTheme(darkMode)}>
        <MaterialReactTable table={table} />
      </ThemeProvider>
    </Box>
  );
};

KenshinTable.propTypes = propTypes;

export default KenshinTable;
